import React, { useMemo, useState } from "react";
import { CATEGORIES, FUNDS, INTAKE_STATUSES, CATEGORY_COLOR } from "./api";
import { formatDate } from "../../lib/format";
import { CustomIcon } from "../shared/CustomIcon";
import { buildCSV, downloadCSV } from "../analytics/_utils";

// Intake Log — incoming donations, with filters and a gold "+ New Intake" button.
export default function IntakeTab({ intakes, currentUser, users, onDelete, onNew, onEdit }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [cat, setCat] = useState("");
  const [fund, setFund] = useState("");
  const [status, setStatus] = useState("");

  const inRange = (s) => {
    if (!s) return true;
    const d = new Date(s);
    if (from && d < new Date(from)) return false;
    if (to && d > new Date(`${to}T23:59:59`)) return false;
    return true;
  };

  const filtered = useMemo(() => {
    return intakes.filter(i =>
      inRange(i.dateReceived) &&
      (!cat || i.category === cat) &&
      (!fund || i.fund === fund) &&
      (!status || i.status === status)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakes, from, to, cat, fund, status]);

  const remove = async (it) => {
    if (!window.confirm(`Remove intake "${it.itemName}"?`)) return;
    await onDelete(it);
  };

  // Per-fund balance — group monetary intakes by fund to surface totals.
  // Non-monetary categories are excluded (only monetary intakes have a
  // numeric value worth summing). Falls back to 0 on missing / NaN values.
  const fundBalances = useMemo(() => {
    const tally = {};
    FUNDS.forEach(f => { tally[f] = 0; });
    (filtered || []).forEach(i => {
      if (i.category !== "Monetary") return;
      const raw = String(i.estimatedValue || "").replace(/[^\d.-]/g, "");
      const v = Number(raw);
      if (Number.isFinite(v) && i.fund) {
        tally[i.fund] = (tally[i.fund] || 0) + v;
      }
    });
    return tally;
  }, [filtered]);

  const exportCsv = () => {
    const headers = ["Date", "Item", "Category", "Fund", "Donor", "Value", "Status", "Logged By"];
    const rows = filtered.map(i => {
      const donor = i._donorRedacted === "no_consent" ? "No consent" : (i._donorRedacted === "private" || i.donorPrivate ? "Anonymous" : (i.donorName || "Anonymous"));
      const loggedBy = (users || []).find(u => u.id === i.loggedById);
      return [i.dateReceived || "", i.itemName, i.category, i.fund, donor, i.estimatedValue || "", i.status, loggedBy ? loggedBy.name : ""];
    });
    downloadCSV("donations-intake.csv", buildCSV({ headers, rows }));
  };

  return (
    <div data-testid="intake-tab">
      {/* Per-fund balance summary — shows running totals of monetary
          contributions within the current filter window. Hidden when
          there are no monetary intakes to keep the layout tight. */}
      {Object.values(fundBalances).some(v => v > 0) && (
        <div className="don-fund-summary" data-testid="intake-fund-summary" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {FUNDS.filter(f => (fundBalances[f] || 0) > 0).map(f => (
            <div key={f} data-testid={`intake-fund-${f}`} style={{ padding: "8px 14px", background: "color-mix(in srgb, var(--kc-accent) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)", borderRadius: 10, fontFamily: "'Montserrat',sans-serif" }}>
              <div style={{ fontSize: 9, color: "color-mix(in srgb, var(--kc-accent) 85%, transparent)", textTransform: "uppercase", letterSpacing: "0.10em", fontWeight: 700 }}>{f}</div>
              <div style={{ fontSize: 16, color: "var(--kc-text)", fontWeight: 700, marginTop: 2 }}>${fundBalances[f].toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}
      <div className="don-toolbar">
        <div className="don-toolbar-filters">
          <label><span className="don-filter-label">From</span><input type="date" className="don-filter-select" data-testid="intake-filter-from" value={from} onChange={e => setFrom(e.target.value)} /></label>
          <label><span className="don-filter-label">To</span><input type="date" className="don-filter-select" data-testid="intake-filter-to" value={to} onChange={e => setTo(e.target.value)} /></label>
          <select className="don-filter-select" data-testid="intake-filter-cat" value={cat} onChange={e => setCat(e.target.value)}>
            <option value="">All categories</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <select className="don-filter-select" data-testid="intake-filter-fund" value={fund} onChange={e => setFund(e.target.value)}>
            <option value="">All funds</option>
            {FUNDS.map(f => <option key={f}>{f}</option>)}
          </select>
          <select className="don-filter-select" data-testid="intake-filter-status" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {INTAKE_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex gap-8">
          <button type="button" className="btn btn-outline" data-testid="intake-export-csv" onClick={exportCsv} disabled={filtered.length === 0}>⬇ CSV</button>
          <button className="btn btn-gold" data-testid="intake-new-btn" onClick={onNew}>+ New Intake</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="don-empty">No intake entries match your filters yet.</div>
      ) : (
        <div className="don-list" data-testid="intake-list">
          {filtered.map(i => {
            const color = CATEGORY_COLOR[i.category] || "var(--kc-accent)";
            const donorLabel = (i._donorRedacted === "no_consent")
              ? "No consent on file"
              : (i._donorRedacted === "private" || i.donorPrivate)
                ? "Anonymous"
                : (i.donorName || "Anonymous");
            const loggedBy = (users || []).find(u => u.id === i.loggedById);
            return (
              <div key={i.id} className="don-row" data-testid={`intake-row-${i.id}`} onClick={() => onEdit(i)}>
                {/* Row 1 — icon + title */}
                <div className="don-row-top">
                  <div className="don-row-icon" style={{ borderColor: color, color, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{categoryIcon(i.category)}</div>
                  <div className="don-row-main">
                    <div className="don-row-title">{i.itemName}</div>
                  </div>
                </div>
                {/* Row 2 — date · donor · value */}
                <div className="don-row-meta">
                  <span>{formatDate(i.dateReceived)}</span>
                  <span>Donor: {donorLabel}</span>
                  {i.estimatedValue && <span>{i.category === "Monetary" ? "" : "≈ "}{i.estimatedValue}</span>}
                  {loggedBy && <span>Logged by {loggedBy.name}</span>}
                </div>
                {/* Row 3 — category · source · status badges */}
                <div className="don-row-chips">
                  <span className="don-chip" style={{ color, background: `${color}15` }}>{i.category}</span>
                  <span className="don-chip" style={{ color: "var(--kc-accent)", background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)" }}>{i.fund}</span>
                  <span className="don-chip" style={{ color: "var(--kc-sub)", background: "rgba(255,255,255,0.06)" }}>{i.status}</span>
                </div>
                {/* Row 4 — actions */}
                <div className="don-row-actions" onClick={e => e.stopPropagation()}>
                  <button className="btn btn-sm btn-outline" data-testid={`intake-edit-${i.id}`} onClick={() => onEdit(i)}>Edit</button>
                  <button className="btn btn-sm btn-danger" data-testid={`intake-delete-${i.id}`} onClick={() => remove(i)}>Delete</button>
                </div>
                {/* Row 5 — visibility indicator */}
                <div className="don-row-visibility" data-testid={`intake-visibility-${i.id}`} style={{ color: i.isPublicListing ? "#4ade80" : "rgba(255,255,255,0.40)" }}>
                  {i.isPublicListing ? "● Public listing" : "○ Private"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function categoryIcon(c) {
  if (c === "Clothing")  return <CustomIcon name="tshirt" size={18} />;
  if (c === "Food")      return <CustomIcon name="food" size={18} />;
  if (c === "Furniture") return <CustomIcon name="armchair" size={18} />;
  if (c === "Monetary")  return <CustomIcon name="money" size={18} />;
  return <CustomIcon name="folder" size={18} />;
}
